import { Heading2, Margin, P, TextLink } from '@ovotech/nebula';
import React, { useContext } from 'react';
import {
  ANNUAL_BOILER_SERVICE_NORMAL_LINK,
  ANNUAL_BOILER_SERVICE_TERMS_LINK,
  ANNUAL_BOILER_SERVICE_DESCRIPTION,
  ANNUAL_BOILER_SERVICE_GET_SERVICE_PLAN,
  ANNUAL_BOILER_SERVICE_INFO_LINK_TEXT,
  ANNUAL_BOILER_SERVICE_TITLE,
  BOILER_LIST,
} from '../../constants';
import { Price } from '../Price';
import Offer from './Offer/Offer';
import { NEW_OFFERS_PAGE } from '@/src/constants/analytics';
import AnnualBoilerServiceImage from '@/src/resources/annual-boiler-service.png';
import { UnorderedListSection } from '@/src/pages/Offers/components/UnorderedListSection';
import { OptimizelyContext } from '@optimizely/react-sdk';
import { OHS_2026_OFFER_CARDS } from '@/src/constants/feature-flags';
import { useAnnualBoilerServiceQuote } from '@/src/hooks/HomeServices/useAnnualBoilerServiceQuote';
import { useCustomerClassificationV2 } from '@/src/hooks/HomeServices/useCustomerClassificationV2';

interface AnnualBoilerServiceProps {
  accountId: string;
}

export const AnnualBoilerService = ({
  accountId,
}: AnnualBoilerServiceProps) => {
  const { optimizely } = useContext(OptimizelyContext);
  const show2026OfferCards = optimizely?.isFeatureEnabled(OHS_2026_OFFER_CARDS);
  const { price } = useAnnualBoilerServiceQuote();
  const { hasAbs, hasBoilerInsurance } = useCustomerClassificationV2();

  const hasBoilerProduct = hasAbs || hasBoilerInsurance;
  const headlineAnnual = price?.headline.full;

  if (show2026OfferCards && !hasBoilerProduct) {
    return (
      <Offer
        offerId={NEW_OFFERS_PAGE.BOILER_SERVICE_ID + 2026}
        image={AnnualBoilerServiceImage}
        linkUrl={`${process.env.NX_PUBLIC_ABS_SERVICE_PLAN_JOURNEY_URL}/?accountId=${accountId}&promoCode=ABSMYOVO&utm_medium=banner&utm_source=offers-and-upgrades&utm_campaign=abs`}
        linkText={'Claim Offer'}
        lowerBody={<P>Terms and eligibility apply</P>}
      >
        <Heading2>
          Get a boiler service worth £{headlineAnnual} for free
        </Heading2>
        <Margin bottom={4}>
          <UnorderedListSection
            title=""
            list={[
              'Service by Gas Safe registered engineers',
              "Extend your boiler's life",
              'Trusted by thousands of UK homes',
            ]}
          />
        </Margin>
      </Offer>
    );
  }

  return (
    <Offer
      offerId={NEW_OFFERS_PAGE.BOILER_SERVICE_ID}
      image={AnnualBoilerServiceImage}
      linkUrl={`${process.env.NX_PUBLIC_ABS_SERVICE_PLAN_JOURNEY_URL}/?accountId=${accountId}&utm_medium=banner&utm_source=offers-and-upgrades&utm_campaign=abs`}
      linkText={ANNUAL_BOILER_SERVICE_GET_SERVICE_PLAN}
      lowerBody={
        <TextLink
          style={{ textAlign: 'center' }}
          href={ANNUAL_BOILER_SERVICE_NORMAL_LINK}
          opensInNewWindow
        >
          {ANNUAL_BOILER_SERVICE_INFO_LINK_TEXT}
        </TextLink>
      }
    >
      <Heading2>{ANNUAL_BOILER_SERVICE_TITLE}</Heading2>
      <P>{ANNUAL_BOILER_SERVICE_DESCRIPTION}</P>
      <Margin bottom={2}>
        <UnorderedListSection
          title={'Your boiler must be:'}
          list={BOILER_LIST}
        />
      </Margin>
      <Price price={`£${price?.headline.monthly} a month, for 12 months`} />
      <P>
        View the{' '}
        <TextLink href={ANNUAL_BOILER_SERVICE_TERMS_LINK} opensInNewWindow>
          annual boiler service terms
        </TextLink>
      </P>
    </Offer>
  );
};
